/**
 * Builds the App State snapshot attached to in-app bug reports.
 *
 * Extracted from the inline `getAppState` closure in `app/_layout.tsx` so the
 * payload is unit-testable. It used to send `state.mealPlan` verbatim, which
 * carries a full `recipe` object per meal (ingredients, searchText, image URLs)
 * and blew the server's character cap inside the first recipe — so the fields
 * serialized after it were silently dropped. Worse, `inventory` was never
 * captured at all, which is exactly what #625 (a pantry bug) needed.
 *
 * Two rules hold this together:
 *  1. Summaries and bounded samples, never whole slices. A report is a
 *     diagnostic, not a backup.
 *  2. No personal identifiers. `posthog.distinctId` is already the support
 *     join key, so uid / email / displayName / householdId add risk and
 *     nothing else. See PII_PATTERN in the tests.
 *
 * Deliberately structural about its input rather than importing RootState:
 * importing the store pulls languageSlice -> i18n -> expo-localization, which
 * Jest cannot transform.
 */

/** How many active pantry items to name before summarising the rest. */
export const PANTRY_SAMPLE_LIMIT = 25;

/** How many meals to include before summarising the rest. */
export const MEAL_SAMPLE_LIMIT = 30;

interface PantryItemLike {
  name?: string;
  category?: string;
  quantity?: number;
  unit?: string;
  opened?: boolean;
  isExpiringSoon?: boolean;
  status?: 'active' | 'eaten' | 'discarded';
}

interface PlannedMealLike {
  id?: string;
  recipeId?: string;
  recipe?: { title?: string } | null;
  scheduledFor?: string;
  mealType?: string;
  servings?: number;
  ingredientsReviewed?: boolean;
  missingIngredients?: unknown[];
}

interface GroceryItemLike {
  isChecked?: boolean;
}

export interface BugReportStateInput {
  inventory?: {
    items?: PantryItemLike[];
    lastUpdated?: string | null;
    lastCleared?: string | null;
  };
  groceryList?: {
    items?: GroceryItemLike[];
    alreadyHaveItems?: unknown[];
    customItems?: unknown[];
    metrics?: unknown;
    generatedDate?: string | null;
    sourceWeekStart?: string | null;
    isStale?: boolean;
  };
  mealPlan?: {
    weeks?: Record<string, PlannedMealLike[]>;
    currentWeekStart?: string;
  };
  userPreferences?: Record<string, unknown>;
  auth?: { isAuthenticated?: boolean; isGuest?: boolean; householdId?: string | null };
  language?: { currentLanguage?: string };
}

export type AnalyticsContext = { distinctId: string; sessionId: string } | null;

/**
 * Cap an array and, when it was cut, say so alongside it. A silently short
 * list reads as "the user only had 3 things", which is how a diagnostic
 * becomes a wrong diagnosis.
 */
function sample<T, R>(
  items: T[],
  limit: number,
  map: (item: T) => R
): { sample: R[]; sampleTruncated?: true } {
  const taken = items.slice(0, limit).map(map);
  return items.length > limit ? { sample: taken, sampleTruncated: true } : { sample: taken };
}

/**
 * Drop the holes before reading anything out of a persisted array.
 *
 * inventorySlice's `migrateItemStatus` already guards against "null/undefined
 * items from corrupted persisted state", so these holes are real. Reading
 * through one throws, and `gatherContext` in @youbbi/expo-bug-reporter turns
 * any throw here into `appState: null` — silently. That loses the entire
 * snapshot for exactly the users whose data is damaged, and the empty report
 * looks identical to a healthy one. Skip the hole, keep the report.
 */
function present<T>(items: T[] | undefined | null): NonNullable<T>[] {
  return Array.isArray(items) ? (items.filter(Boolean) as NonNullable<T>[]) : [];
}

function buildPantry(inventory: BugReportStateInput['inventory']) {
  const items = present(inventory?.items);
  // `status` is optional and undefined means active — the same rule
  // calculateRequiredQuantities uses. Do not tighten it to === 'active'.
  const active = items.filter(i => !i.status || i.status === 'active');

  return {
    total: items.length,
    active: active.length,
    eaten: items.filter(i => i.status === 'eaten').length,
    discarded: items.filter(i => i.status === 'discarded').length,
    lastUpdated: inventory?.lastUpdated ?? null,
    lastCleared: inventory?.lastCleared ?? null,
    ...sample(active, PANTRY_SAMPLE_LIMIT, i => ({
      name: i.name,
      category: i.category,
      quantity: i.quantity,
      unit: i.unit,
      opened: i.opened,
      isExpiringSoon: i.isExpiringSoon,
    })),
  };
}

function buildGroceryList(groceryList: BugReportStateInput['groceryList']) {
  const items = present(groceryList?.items);
  return {
    itemCount: items.length,
    checkedCount: items.filter(i => i.isChecked).length,
    alreadyHaveCount: present(groceryList?.alreadyHaveItems).length,
    customItemCount: present(groceryList?.customItems).length,
    sourceWeekStart: groceryList?.sourceWeekStart ?? null,
    generatedDate: groceryList?.generatedDate ?? null,
    isStale: groceryList?.isStale ?? false,
    metrics: groceryList?.metrics ?? null,
  };
}

/**
 * Weeks nearest the one the user is looking at, first.
 *
 * The sample used to follow storage order, which is roughly oldest-first: a
 * user with months of history could file a report about the week on their
 * screen and have that week fall entirely outside the cap, while `weekCount`
 * made the payload look complete. Ties break toward the later week, and with
 * no viewed week to anchor on, most-recent-first still beats storage order.
 */
function orderWeeks(
  weeks: Record<string, PlannedMealLike[]>,
  currentWeekStart: string | undefined
): [string, PlannedMealLike[]][] {
  const pivot = currentWeekStart ? Date.parse(currentWeekStart) : NaN;

  return Object.entries(weeks).sort(([a], [b]) => {
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    // An unparseable key sorts last rather than poisoning the comparison.
    if (Number.isNaN(ta) || Number.isNaN(tb)) {
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      return Number.isNaN(ta) ? 1 : -1;
    }
    if (Number.isNaN(pivot)) return tb - ta;
    return Math.abs(ta - pivot) - Math.abs(tb - pivot) || tb - ta;
  });
}

/**
 * Meals without their recipe bodies. The title is kept because it is what
 * makes a report legible; ingredients, searchText and image URLs are the bulk
 * and are never needed to reproduce a planner or grocery bug.
 */
function buildMealPlan(mealPlan: BugReportStateInput['mealPlan']) {
  const weeks = mealPlan?.weeks ?? {};
  const flattened = orderWeeks(weeks, mealPlan?.currentWeekStart).flatMap(([weekStart, meals]) =>
    present(meals).map(meal => ({
      weekStart,
      id: meal.id,
      recipeId: meal.recipeId,
      title: meal.recipe?.title,
      scheduledFor: meal.scheduledFor,
      mealType: meal.mealType,
      servings: meal.servings,
      ingredientsReviewed: meal.ingredientsReviewed,
      missingIngredientCount: (meal.missingIngredients ?? []).length,
    }))
  );

  return {
    currentWeekStart: mealPlan?.currentWeekStart ?? null,
    weekCount: Object.keys(weeks).length,
    mealCount: flattened.length,
    ...sample(flattened, MEAL_SAMPLE_LIMIT, m => m),
  };
}

/**
 * Preferences minus the two unbounded history arrays, which grow with every
 * meal picked and never help diagnose anything on their own.
 */
function buildPreferences(prefs: BugReportStateInput['userPreferences']) {
  if (!prefs) return null;
  const { rejectedRecipes, acceptedRecipes, ...rest } = prefs as Record<string, unknown>;
  return {
    ...rest,
    rejectedCount: Array.isArray(rejectedRecipes) ? rejectedRecipes.length : 0,
    acceptedCount: Array.isArray(acceptedRecipes) ? acceptedRecipes.length : 0,
  };
}

export function buildBugReportState(
  state: BugReportStateInput | null | undefined,
  analyticsContext: AnalyticsContext,
  /**
   * The receipt scan on screen, or the last one. Its id points at the record
   * `/api/receipt-scan` keeps (band reads, stitched list, photo), so a report
   * like #750 arrives with its own reproduction instead of a screenshot. The id
   * is random and names nobody.
   */
  lastReceiptScan?: { scanId: string; at: string } | null
): Record<string, unknown> {
  const s = state ?? {};

  return {
    pantry: buildPantry(s.inventory),
    groceryList: buildGroceryList(s.groceryList),
    mealPlan: buildMealPlan(s.mealPlan),
    userPreferences: buildPreferences(s.userPreferences),
    session: {
      // Booleans and a locale only. Never the uid, email, display name, or
      // household id — `posthog.distinctId` below is the support join key.
      isAuthenticated: s.auth?.isAuthenticated ?? false,
      isGuest: s.auth?.isGuest ?? false,
      hasHousehold: Boolean(s.auth?.householdId),
      language: s.language?.currentLanguage ?? null,
    },
    ...(lastReceiptScan ? { lastReceiptScan } : {}),
    ...(analyticsContext ? { posthog: analyticsContext } : {}),
  };
}
